import React, { useState } from "react";
import Thumbnail from "@/components/Thumbnail";
import HostingInfoDialog from "@/components/HostingInfoDialog";
import { classifyHosting, getHostingLabel } from "@/lib/hosting";
import { HOSTING } from "@/lib/constants";

function statusClass(status) {
  if (status === "Live") return "live";
  if (status === "In Arbeit") return "work";
  return "arch";
}

function formatDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d.length === 10 ? d + "T00:00:00" : d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function prettyHost(url) {
  if (!url) return "";
  try {
    const u = new URL(url.startsWith("http") ? url : "https://" + url);
    return u.host.replace(/^www\./, "") + (u.pathname && u.pathname !== "/" ? u.pathname : "");
  } catch {
    return url;
  }
}

export default function EntryCard({ entry, onEdit, onDelete }) {
  const [showInfo, setShowInfo] = useState(false);
  const cls = statusClass(entry.status);
  const hosting = classifyHosting(entry.url);
  const hostingLabel = getHostingLabel(hosting);
  const hostingClass = `host-badge host-${hosting}`;

  const onBadgeClick = (e) => {
    e.stopPropagation();
    if (hosting === HOSTING.EMERGENT) setShowInfo(true);
  };

  return (
    <article className="card" data-testid="entry-card">
      <Thumbnail url={entry.url} name={entry.name || "?"} />
      <div className="card-body">
        <div className="card-top">
          <h3 data-testid="entry-name">{entry.name || "Ohne Namen"}</h3>
          <div className="status" data-testid="entry-status">
            <span className={`dot ${cls}`} />
            {entry.status || "—"}
          </div>
        </div>
        <div className="tags">
          {entry.type && <span className="tag tag-typ">{entry.type}</span>}
          <button
            type="button"
            className={hostingClass}
            onClick={onBadgeClick}
            title={
              hosting === HOSTING.EMERGENT
                ? "Klicken: Eigene Domain verbinden (Anleitung)"
                : hosting === HOSTING.OWN
                ? "Diese App läuft unter eigener Domain"
                : "Noch keine URL hinterlegt"
            }
            data-testid={`hosting-badge-${hosting}`}
            disabled={hosting !== HOSTING.EMERGENT}
          >
            <span aria-hidden>{hostingLabel.icon}</span>
            <span>{hostingLabel.text}</span>
          </button>
        </div>
        {entry.description && <p className="desc">{entry.description}</p>}
        <div className="meta">
          {entry.platform && (
            <div>
              <b>Veröffentl.</b>
              <span>{entry.platform}</span>
            </div>
          )}
          {entry.date && (
            <div>
              <b>Datum</b>
              <span>{formatDate(entry.date)}</span>
            </div>
          )}
        </div>
        {entry.note && <div className="note-row">{entry.note}</div>}
        <div className="url-row">
          {entry.url ? (
            <a
              href={entry.url.startsWith("http") ? entry.url : "https://" + entry.url}
              target="_blank"
              rel="noopener noreferrer"
              title={entry.url}
              className="url-pill"
              data-testid="entry-url"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span>{prettyHost(entry.url)}</span>
            </a>
          ) : (
            <span style={{ color: "var(--faint)", fontSize: 12.5, fontStyle: "italic" }}>
              keine URL hinterlegt
            </span>
          )}
          <div className="actions">
            <button
              className="icon-btn"
              onClick={onEdit}
              title="Bearbeiten"
              data-testid="edit-btn"
              aria-label="Bearbeiten"
            >
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
              </svg>
            </button>
            <button
              className="icon-btn danger"
              onClick={onDelete}
              title="Löschen"
              data-testid="delete-btn"
              aria-label="Löschen"
            >
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="m6 6 1 14h10l1-14" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showInfo && <HostingInfoDialog onClose={() => setShowInfo(false)} />}
    </article>
  );
}
